

<template>
  <div>
    <div class="form-group row password-notifications">
      <p :class="['bg-danger', 'pppoe-warning', 'col-md-12']" v-if="!hidePasswordNotifications"><b>NOTE:</b> Customers configured for PPPoE must also update their router password.</p>
    </div>

    <div class="form-group" v-if="requireOldPassword">
      <show-password-field v-model="oldPassword"
                           :label="'Current password'"
                           :name="'password'"
                           :required="true"
                           :maxlength="passwordMaxLength"/>
      <small class="pw-warnings" v-if="!oldPasswordIsValid">
        <b class="text-danger">
          You must enter your current password
        </b>
      </small>
    </div>
    <div class="form-group row">
      <div v-if="suggestionsInModal" class="col-md-6">
        <b-button variant="light" id="pw-help" @click="modalVisible = true">Help me create a secure password</b-button>
      </div>
      <div v-else :class="!passwordInModal ? 'col-md-6' : 'col-md-12'">
        <div>
          <b-card no-body id="passGen" header-text-variant="white">
            <template #header >
              <div class="btn btn-primary btn-block collapsed" @click="collapseVisible = !collapseVisible">Help me create a secure password <span class="float-right"><i :class="['bi', collapseVisible ? 'bi-caret-up-fill' : 'bi-caret-down-fill']"/></span></div>
            </template>
            <b-collapse id="password-collapse" v-model="collapseVisible">
              <div class="card-body">
<!--                <password-generator :password-label="passwordLabel" @password-selected="generatedPasswordSelected"/>-->
                <password-generator :password-label="passwordLabel"/>
              </div>
            </b-collapse>
          </b-card>
        </div>
      </div>

    </div>
    <form-validator v-model="formValid">
      <form @submit.prevent>
        <div class="row">
          <div class="col-md-6">
            <show-password-field
              class="mb-2"
              v-model="password"
              @toggle="toggleShowNewPass($event)"
              :label="passwordLabel"
              :help="passwordHelp"
              :minlength="passwordMinLength"
              :maxlength="passwordMaxLength"
              :required="true"
              name="new-password"
              ref="newPassword"
            ></show-password-field>
            <small class="faded">Password strength</small>
            <b-progress :value="passwordComplexity" striped height="10px" variant="success" animated/>
            <div class="pw-warnings">
              <div class="mb-1" v-if="dangerSuggestion" >
                <b class="text-danger">
                  <b-icon-x-lg scale="0.75"/>&nbsp;{{ dangerSuggestion }}
                </b>
              </div>
              <div class="mb-1" v-if="warningSuggestion" >
                <b class="text-warning">
                  <b-icon-info-circle-fill/>&nbsp;{{ warningSuggestion }}
                </b>
              </div>
            </div>
          </div>
          <div class="col-md-6 form-group">
            <xm-input class="mb-2" name="new-password-confirm" :type="showNewPass?'text':'password'" v-model="newPassConfirm" required :maxlength="passwordMaxLength" :label="passwordConfirmLabel"/>
            <span v-if="!passwordsMatch" class="text-muted"><b style="color:red;"><b-icon-x-lg scale="0.75"/>&nbsp;Passwords do not match.</b></span>
          </div>
        </div>
      </form>
    </form-validator>
    <b-modal
      title="Generate Password"
      id="password-generator-modal"
      size="lg"
      v-model="modalVisible">
<!--      <password-generator :password-label="passwordLabel" @password-selected="generatedPasswordSelected"/>-->
      <password-generator :password-label="passwordLabel"/>
    </b-modal>
  </div>
</template>

<script>
import FormValidator from './FormValidator.vue'
import { BCard, BCollapse, BProgress, BModal, BButton, BIconXLg, BIconInfoCircleFill, BIconCaretDownFill, BFormInput } from 'bootstrap-vue'
import ShowPasswordField from './ShowPasswordField'
import PasswordGenerator from './PasswordGenerator'
import XmInput from './XmInput.vue'
import helpers from "../lib/helpers";
const zxcvbn = require('zxcvbn')

export default {
  emits: ['form-state', 'input'],
  data() {
    return {
      newPassConfirm: '',
      showNewPass: false,
      formValid: false,
      showSuggestions: false,
      passwordMinLength: 12,
      passwordMaxLength: 127,
      passwordMinComplexity: 100,
      passwordGeneratorOpen: false,
      password: this.value,
      oldPassword: '',
      collapseVisible: false,
      modalVisible: false,
      dangerSuggestion: '',
      warningSuggestion: '',
    }
  },
  props: {
    value: {
      default: '',
      type: String
    },
    suggestionsInModal: {
      default: false,
      type: Boolean
    },
    requireOldPassword: {
      default: false,
      type: Boolean
    },
    isNewPassword: {
      default: true,
      type: Boolean
    },
    passwordInModal: {
      default: false,
      type: Boolean
    },
    hidePasswordNotifications: {
      default: false,
      type: Boolean
    }
  },

  computed: {
    formData() {
      return {
        old_password: this.oldPassword,
        password: this.password,
      }
    },
    passwordLabel() {
      return this.isNewPassword ? 'New password' : 'Password';
    },
    passwordConfirmLabel() {
      return this.isNewPassword ? 'Confirm new password' : 'Confirm password';
    },
    passwordHelp() {
      return 'Enter a password from ' + this.passwordMinLength + ' to ' + this.passwordMaxLength + ' characters';
    },
    passwordsMatch() {
      return this.password === this.newPassConfirm;
    },
    newOldPasswordsMatch() {
      if (!this.requireOldPassword) {
        return false;
      }
      return this.password === this.oldPassword;
    },
    passwordLength() {
      return this.password.length;
    },
    passwordShort() {
      if (this.passwordLength === 0 || this.newPassConfirm === 0) {
        return false;
      }
      return this.passwordLength < this.passwordMinLength;
    },
    passwordLong() {
      return this.passwordLength > this.passwordMaxLength;
    },
    passwordLengthOk() {
      return this.passwordLength >= this.passwordMinLength &&
        this.passwordLength <= this.passwordMaxLength;
    },
    passwordStats() {
      return zxcvbn(this.password);
    },
    passwordComplexity() {
      return this.passwordStats.score * 25;
    },
    passwordComplexityPassed() {
      if (this.passwordLength === 0 || this.newPassConfirm === 0) {
        return true;
      }
      return this.passwordComplexity >= this.passwordMinComplexity;
    },
    passwordIsValid() {
      return this.passwordsMatch && this.passwordComplexityPassed && this.passwordLengthOk;
    },
    oldPasswordIsValid() {
      return this.oldPassword.length > 0;
    },
    formIsValid() {
      if (this.requireOldPassword && (!this.oldPasswordIsValid || this.newOldPasswordsMatch)) {
        return false;
      }
      return this.passwordIsValid && this.formValid;
    }
  },
  methods: {
    toggleShowNewPass(val) {
      this.showNewPass = val;
    },
    setDangerSuggestion() {
      if (!this.showSuggestions) {
        return '';
      }
      let danger_suggestions = [];

      if (!this.passwordComplexityPassed) {
        danger_suggestions.push('Password score too low.');
      }
      if (this.passwordShort) {
        danger_suggestions.push('Password too short.');
      }
      if (this.passwordLong) {
        danger_suggestions.push('Password too long.');
      }
      if (this.requireOldPassword && this.newOldPasswordsMatch) {
        danger_suggestions.push('New password must be different from current password.');
      }
      this.dangerSuggestion = danger_suggestions.join(' ').trim();
    },
    setWarningSuggestion() {
      if (!this.showSuggestions) {
        return '';
      }
      let warning_suggestion = '';
      let warning_suggestions = [];
      if (!this.passwordComplexityPassed && this.passwordStats.feedback.warning) {
        warning_suggestions.push(this.passwordStats.feedback.warning);
      }
      for (let suggestion of this.passwordStats.feedback.suggestions) {
        warning_suggestions.push(suggestion);
      }
      for (let suggestion of warning_suggestions) {
        if (suggestion.slice(-1) !== '.') {
          suggestion += '.';
        }
        warning_suggestion += suggestion + ' ';
      }
      this.warningSuggestion = warning_suggestion.trim();
    },
    generatedPasswordSelected(password) {
      // if this is unused in the future after getting some sort of copy-paste behavior closing the modal/collapse
      // that doesn't use this function, delete it.
      this.password = password;
      if (this.suggestionsInModal) {
        this.modalVisible = false;
      } else {
        this.collapseVisible = false;
      }
    }
  },
  watch: {
    passwordLength(new_val) {
      if (!this.showSuggestions) {
        if (new_val > 0) {
          this.showSuggestions = true;
        }
      }
    },
    formIsValid(new_val) {
      this.$emit('form-state', new_val);
    },
    formData: {
      handler(new_val, old_val) {
        this.$emit('input', new_val);
      },
      deep: true
    },
    password (new_val, old_val) {
      helpers.debounce(this.setDangerSuggestion, 750)();
      helpers.debounce(this.setWarningSuggestion, 750)();
    },
    oldPassword (new_val, old_val) {
      helpers.debounce(this.setDangerSuggestion, 750)();
      helpers.debounce(this.setWarningSuggestion, 750)();
    }

  },
  components: {
    BCard,
    BCollapse,
    BProgress,
    FormValidator,
    ShowPasswordField,
    PasswordGenerator,
    BIconXLg,
    BIconInfoCircleFill,
    BIconCaretDownFill,
    BModal,
    BButton,
    XmInput,
    BFormInput
  },

}
</script>

<style scoped>
#passGen > .card-header {
  padding: 0;
  border: 0;
}

#passGen > .card-header:hover {
  cursor: pointer;
  padding: 0;
  border: 0;
}

#passGen > .card-header h4 {
  color: white
}

.pw-warnings {
  font-size: 14px;
  padding-top: 10px;
}

.card-header-button {
  border: 0;
  margin: 0;
  border-radius: 0 !important;
}

.pppoe-warning {
  padding: 0.5em 1em;
  margin-bottom: 1.5em;
  color: white;
}

.password-notifications {
  margin: 0;
}

</style>



// WEBPACK FOOTER //
// PasswordForm.vue?a33da662